import React from 'react';
import Input from 'jl-design-system/elements/input/Input';
import InputManageCursor from 'jl-design-system/elements/input-manage-cursor/InputManageCursor';
import { creditCardNumberCursorHelper } from 'jl-design-system/form/helpers/manageCursor';
import {
  formatCardNumberAmex,
  formatCardNumberDefault,
} from 'jl-design-system/form/normalizers/formatCardNumber';
import formatExpiryDate from 'jl-design-system/form/normalizers/formatExpiryDate';
import getRegExp, {
  numbersPattern,
  expiryDateRegex,
  lettersSingleDashApostropheSpaceRegex,
} from 'jl-design-system/form/regex';
import {
  numbersOnly,
  excludeSymbolsNamesDataStandards,
} from 'jl-design-system/form/normalizers/restrictInput';
import InputCreditCard from '../../../widgets/form/fields/input-credit-card/InputCreditCard';
import InputSecurityCode from '../../../widgets/form/fields/input-security-code/InputSecurityCode';

import { getCardNumberInputClassName, getCvvClassName } from '../helpers/creditCardInputStyles';
import supportedCreditCardTypes from '../../../constants/supportedCreditCardTypes';

export const CARD_NUMBER = {
  component: InputCreditCard,
  name: 'cardNumber',
  id: 'cardNumber',
  type: 'tel',
  label: 'Card number',
  className: 'fieldLarge',
  required: true,
  manageCursorHelper: creditCardNumberCursorHelper,
  autoComplete: 'cc-number',
};

export const CARD_NAME = {
  component: Input,
  name: 'cardholderName',
  label: (
    <>Name on card <span>(As written on card)</span></>
  ),
  id: 'cardholderName',
  type: 'text',
  className: 'fieldLarge',
  required: true,
  maxLength: 50,
  validationMessageKeys: {
    required: 'Please enter the name on the front of your card',
    maxLength: "Sorry, you've entered a name longer than we can accept",
    regex: 'Please remove the invalid characters to continue',
  },
  regex: getRegExp(lettersSingleDashApostropheSpaceRegex),
  normalize: excludeSymbolsNamesDataStandards,
  autoComplete: 'cc-name',
};

export const CARD_EXPIRY_DATE = {
  component: InputManageCursor,
  name: 'expiryDate',
  label: 'Expiry date',
  id: 'expiryDate',
  type: 'tel',
  className: 'fieldSmall',
  dateFormat: 'MM-YY',
  placeholder: 'MM/YY',
  dateNotExpired: 'MM-YY',
  required: true,
  maxLength: 5,
  validationMessageKeys: {
    required: 'Please enter the expiry date',
    regex: 'Please enter a valid expiry date in format MM/YY',
    dateFormat: 'Please enter a valid expiry date in format MM/YY',
    dateNotExpired: 'This date has expired',
  },
  regex: expiryDateRegex,
  normalize: formatExpiryDate,
  autoComplete: 'cc-exp',
};

export const CARD_SECURITY_CODE = {
  component: InputSecurityCode,
  type: 'tel',
  name: 'securityCode',
  label: 'Security code',
  id: 'securityCode',
  className: 'fieldMediumInputSmall',
  required: true,
  regex: getRegExp(numbersPattern),
  normalize: numbersOnly,
  autoComplete: 'cc-csc',
};

export const CARD_DETAILS_FORM_ID = 'cardDetailsForm';

export function getCardDetailsFormConfig({
  cardType,
  cardMaxLength,
  cardMinLength,
  securityCodeLength,
}) {
  const config = {
    id: CARD_DETAILS_FORM_ID,
    fields: [
      {
        ...CARD_NUMBER,
        inputWrapperClassName: getCardNumberInputClassName(cardType),
        normalize: (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS) ?
          formatCardNumberAmex : formatCardNumberDefault,
        minLength: cardMinLength,
        luhnNumberCheck: cardMinLength,
        maxLength: (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS) ? cardMaxLength + 2 : cardMaxLength + 4,
        validationMessageKeys: {
          required: 'Please enter the card number on the front of your card',
          minLength: (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS) ?
            'Please enter the 15 digit card number on the front of your card' :
            "The card number you've entered is too short",
          luhnNumberCheck: "We don't recognise your card number. Please check and try again",
        },
      },
      CARD_NAME,
      {
        ...CARD_EXPIRY_DATE,
        normalize: formatExpiryDate,
      },
    ],
  };

  if (cardType === supportedCreditCardTypes.BUSINESS_ACCOUNT) {
    return config;
  }

  config.fields = [
    ...config.fields,
    {
      ...CARD_SECURITY_CODE,
      inputWrapperClassName: getCvvClassName(cardType),
      minLength: securityCodeLength,
      maxLength: securityCodeLength,
      validationMessageKeys: {
        required: (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS) ?
          'Please enter the 4 digits on the front of your card above the long card number' :
          'Please enter the last 3 digits found on the back of your card',
        minLength: (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS) ?
          'Please enter the 4 digits on the front of your card above the long card number' :
          'Please enter the last 3 digits found on the back of your card',
      },
      instruction: (cardType === supportedCreditCardTypes.AMERICAN_EXPRESS) ?
        'This is the 4 digits on the front of the card above the long card number' :
        'This is the last 3 digits on the back of your card',
    },
  ];

  return config;
}
